/**
 * 학생/학급별 배포 과제 목록 + 캘린더 이벤트 생성
 *
 * 데이터 소스:
 * - 일반: ReportAssignLesson/{lesson_id}/Report
 * - 보충: ReportAssignStudent/{student_uid}/Report (isSupplementary=true, 학급 불필요)
 *
 * 반복 배포의 순서를 보존하기 위해 시작일 제한 없이 할당 목록을 조회한다.
 */
import { useEffect, useRef, useState } from "react";
import useFirestore from "shared/hooks/useFirestore";
import { QueryDocumentSnapshot } from "firebase/firestore";
import { ContentSettings } from "shared/types/Homework";
import useGetBranchId from "shared/hooks/useGetBranchId";
import {
    buildReportProgressContext,
    calculateReportProgressWithContext,
    ReportProgressContext,
} from "shared/utils/reportProgress";
import { getReportFolderPath } from "shared/utils/reportFolderUtils";
import { mapWithConcurrency } from "shared/utils/mapWithConcurrency";
import { createFirestoreReadCache } from "../homeworkReadCache";
import { HOMEWORK_CALENDAR_CONCURRENCY, scheduleHomeworkAssignments } from "../homeworkCalendarSchedule";

type FirestoreReaders = Pick<ReturnType<typeof useFirestore>, "readFirestoreOneDoc" | "readFirestore">;
type ProgressContextCache = Map<string, Promise<ReportProgressContext>>;

interface StudentReport {
    id: string;
    path: string;
    release_id: string;
    title: string;
    date: Date;
    progress?: number;
    contentProgress?: {
        [key: string]: number;
    };
    folderPath?: string[];
    report_setting?: ContentSettings | null;
}

interface ReportData {
    id: string;
    release_id: string;
    name: string;
    path: string;
    progress?: number;
}

interface AssignedData {
    release_id: string;
    distribution_id?: string;
    dayOfWeek: boolean[];
    interval: number;
    order: number;
    date: string;
    effective_date: {
        start: string;
        end: string;
    };
    report: {
        path: string;
        id: string;
        order: number;
    };
    report_setting?: ContentSettings | null;
}

const useHomeworkStudentReportView = (
    selected_lesson: string,
    selected_student?: string,
    selected_month?: Date
) => {
    const {readFirestoreOneDoc, readFirestore} = useFirestore();
    const {getBranchId} = useGetBranchId();
    const [reportList, setReportList] = useState<StudentReport[]>([])
    const [isOpenLoadingModal, setIsOpenLoadingModal] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isSupplementary, setIsSupplementary] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [retryVersion, setRetryVersion] = useState(0);
    const requestIdRef = useRef(0);
    const selectedMonthKey = selected_month
        ? `${selected_month.getFullYear()}-${selected_month.getMonth()}`
        : undefined;

    // 보충 과제는 학급 없이 조회 가능, 일반 과제는 selected_lesson 필수
    useEffect(() => {
        const requestId = ++requestIdRef.current;
        setReportList([]);
        setLoadError(null);
        if (selected_student && (isSupplementary || selected_lesson)) {
            getReportList(requestId);
        } else {
            setIsOpenLoadingModal(false);
            setLoadingProgress(0);
        }
        return () => { requestIdRef.current = requestId + 1; };
    }, [selected_lesson, selected_student, isSupplementary, selectedMonthKey, retryVersion])

    const getReportProgress = async(
        reportData: Pick<ReportData, "id" | "path" | "release_id">,
        student_uid: string,
        readers: FirestoreReaders,
        report_setting: ContentSettings | null | undefined,
        branch_id: string,
        contextCache: ProgressContextCache
    ) => {
        const cacheKey = JSON.stringify([reportData.path, reportData.id, report_setting ?? null]);
        if (!contextCache.has(cacheKey)) {
            contextCache.set(
                cacheKey,
                buildReportProgressContext(readers, reportData, report_setting, branch_id)
            );
        }
        return calculateReportProgressWithContext(
            readers.readFirestoreOneDoc,
            reportData.release_id,
            student_uid,
            await contextCache.get(cacheKey)!
        );
    };

    // 리포트 데이터 가져오기
    const getReport = async(
        assigned:{release_id:string, id:string, path:string, report_setting?: ContentSettings | null},
        readers: FirestoreReaders,
        branch_id: string,
        contextCache: ProgressContextCache
    ): Promise<ReportData | null> => {
        try {
            // 진행률은 문서 제목에 의존하지 않아 메타데이터 조회와 동시에 계산한다.
            const [reportDoc, contentQuery, progress] = await Promise.all([
                readers.readFirestoreOneDoc(`${assigned.path}`, assigned.id),
                readers.readFirestore(`${assigned.path}/${assigned.id}/Content`),
                selected_student
                    ? getReportProgress(assigned, selected_student, readers, assigned.report_setting, branch_id, contextCache)
                    : Promise.resolve(undefined)
            ]);

            if (!reportDoc.exists() || contentQuery.empty) {
                return null;
            }

            return {
                id: reportDoc.id,
                release_id: assigned.release_id,
                name: reportDoc.data().name,
                path: assigned.path,
                progress
            };
        } catch (error) {
            console.error('Error fetching report:', error);
            return null;
        }
    };

    // 할당된 리포트 데이터 처리
    const processAssignedData = async (
        assigned_data: AssignedData & { scheduledDate: Date },
        branch_id: string,
        readers: FirestoreReaders,
        contextCache: ProgressContextCache
    ): Promise<StudentReport> => {
        const [report_data, folderPath] = await Promise.all([
            getReport({
                release_id: assigned_data.release_id,
                id: assigned_data.report.id,
                path: assigned_data.report.path,
                report_setting: assigned_data.report_setting
            }, readers, branch_id, contextCache),
            getReportFolderPath(readers.readFirestoreOneDoc, assigned_data.report.path).catch(error => {
                console.error('Error fetching report folder:', error);
                return [] as string[];
            })
        ]);
        
        return {
            release_id: assigned_data.release_id,
            path: assigned_data.report.path,
            id: assigned_data.report.id,
            title: report_data?.name || '',
            folderPath,
            date: assigned_data.scheduledDate,
            report_setting: assigned_data.report_setting,
            progress: report_data?.progress ?? 0
        };
    };

    // 리포트 목록 가져오기
    const getReportList = async(requestId: number) => {
        setIsOpenLoadingModal(true);
        setLoadingProgress(0);
        
        try {
            const readers = createFirestoreReadCache({ readFirestoreOneDoc, readFirestore });
            const contextCache: ProgressContextCache = new Map();
            const [branch_id, assigned_list] = await Promise.all([
                getBranchId(),
                getAssignedReportData(readers)
            ]);
            if (requestId !== requestIdRef.current) return;
            const scheduledReports = scheduleHomeworkAssignments(assigned_list, selected_month);
            const totalReports = scheduledReports.length;
            let processedReports = 0;

            const reports = await mapWithConcurrency(scheduledReports, async (assigned_data) => {
                if (requestId !== requestIdRef.current) return null;
                const report = await processAssignedData(assigned_data, branch_id, readers, contextCache);
                processedReports++;
                if (requestId === requestIdRef.current) {
                    setLoadingProgress((processedReports / totalReports) * 100);
                }
                return report;
            }, HOMEWORK_CALENDAR_CONCURRENCY);

            if (requestId !== requestIdRef.current) return;
            setReportList(reports.filter((report): report is StudentReport => report !== null));
        } catch (error) {
            console.error('Error fetching report list:', error);
            if (requestId === requestIdRef.current) {
                setReportList([]);
                setLoadError('과제 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setIsOpenLoadingModal(false);
                setLoadingProgress(0);
            }
        }
    };

    // 할당된 리포트 데이터 가져오기
    const getAssignedReportData = async(readers: FirestoreReaders) => {
        if(isSupplementary){
            return await fetchAssignedData(
                `ReportAssignStudent/${selected_student}/Report`, readers
            );
        }

        if (!selected_lesson) {
            return [];
        }
        
        return await fetchAssignedData(
            `ReportAssignLesson/${selected_lesson}/Report`, readers
        );
    };

    // 할당된 데이터 가져오기 (공통 로직)
    const fetchAssignedData = async (collectionPath: string, readers: FirestoreReaders): Promise<AssignedData[]> => {
        const response = await readers.readFirestore(collectionPath);
        if (!response || !Array.isArray(response.docs)) {
            throw response instanceof Error ? response : new Error('Invalid assignment response');
        }

        if (response.empty) {
            return [];
        }

        return response.docs.map((doc: QueryDocumentSnapshot) => {
            const assigned_data = doc.data();
            return {
                release_id: doc.id,
                distribution_id: assigned_data.distribution_id,
                effective_date: assigned_data.effective_date ?? { start: "", end: "" },
                dayOfWeek: Array.isArray(assigned_data.dayOfWeek) ? assigned_data.dayOfWeek : [],
                order: assigned_data.report?.order ?? assigned_data.order ?? 0,
                interval: assigned_data.interval,
                date: assigned_data.date ?? "",
                report: {
                    path: assigned_data.report?.path ?? "",
                    id: assigned_data.report?.id ?? "",
                    order: assigned_data.report?.order ?? assigned_data.order ?? 0
                },
                report_setting: assigned_data.report_setting
            };
        });
    };

    return {
        reportList, 
        isOpenLoadingModal, 
        loadingProgress,
        loadError,
        retryLoad: () => setRetryVersion(version => version + 1),
        isSupplementary,
        setIsSupplementary
    };
}

export default useHomeworkStudentReportView;
